import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api/client';
import type { BillingPocket } from '../api/types';
import { Card, Badge, Alert } from '../components/ui';

interface BuildingAsset {
  id: string;
  name: string;
}

interface BuildingGroup {
  building_asset_id: string;
  building_name: string;
  pockets: BillingPocket[];
  total_meters: number;
}

export default function Pockets() {
  const [pockets, setPockets] = useState<BillingPocket[]>([]);
  const [buildings, setBuildings] = useState<BuildingAsset[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  // Create Pocket modal
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState({ name: '', description: '', building_asset_id: '', shop_no: '' });
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [pocketsRes, buildingsRes] = await Promise.all([
        api.getPockets(),
        api.getBuildings(),
      ]);
      setPockets(pocketsRes.data);
      setBuildings(buildingsRes.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // Group pockets by building
  const buildingGroups: BuildingGroup[] = (() => {
    const grouped = new Map<string, BillingPocket[]>();
    for (const pocket of pockets) {
      const key = pocket.building_asset_id;
      if (!grouped.has(key)) grouped.set(key, []);
      grouped.get(key)!.push(pocket);
    }

    return Array.from(grouped.entries()).map(([buildingId, buildingPockets]) => ({
      building_asset_id: buildingId,
      building_name: buildings.find(b => b.id === buildingId)?.name || buildingId.substring(0, 16) + '...',
      pockets: buildingPockets,
      total_meters: buildingPockets.reduce((sum, p) => sum + (p.meter_count || 0), 0),
    })).sort((a, b) => a.building_name.localeCompare(b.building_name));
  })();

  const filteredGroups = buildingGroups.filter(g => {
    if (!search) return true;
    const q = search.toLowerCase();
    return g.building_name.toLowerCase().includes(q) ||
      g.pockets.some(p => p.name.toLowerCase().includes(q) || (p.active_tenant_name || p.tenant_name || '').toLowerCase().includes(q));
  });

  const handleCreatePocket = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setCreating(true);
      setCreateError(null);
      await api.createPocket({
        name: createForm.name,
        description: createForm.description || undefined,
        building_asset_id: createForm.building_asset_id,
        shop_no: createForm.shop_no || undefined,
      });
      setShowCreateModal(false);
      setCreateForm({ name: '', description: '', building_asset_id: '', shop_no: '' });
      await loadData();
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : 'Failed to create pocket');
    } finally {
      setCreating(false);
    }
  };

  if (loading) {
    return (
      <div className="p-4 lg:p-8">
        <div className="animate-pulse space-y-4">
          {[1, 2, 3].map(i => <div key={i} className="h-32 bg-border-token rounded-xl"></div>)}
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 lg:p-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-content">Buildings & Pockets</h1>
          <p className="text-content-muted mt-1">{buildingGroups.length} building{buildingGroups.length !== 1 ? 's' : ''} | {pockets.length} pocket{pockets.length !== 1 ? 's' : ''}</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="px-4 py-2 min-h-tap lg:min-h-0 inline-flex items-center justify-center bg-accent text-accent-fg rounded-lg hover:opacity-90 transition-colors font-medium"
        >
          Create Pocket
        </button>
      </div>

      {/* Error */}
      {error && (
        <Alert tone="danger" className="mb-6">
          {error}
        </Alert>
      )}

      {/* Search */}
      <div className="mb-6">
        <input
          type="text"
          placeholder="Search buildings or pockets..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="px-3 py-2 min-h-tap lg:min-h-0 border border-border-token rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 w-full sm:w-72"
        />
      </div>

      {/* Building Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredGroups.length === 0 ? (
          <Card className="col-span-full shadow-sm p-12 text-center text-content-muted">
            <p className="text-lg font-medium mb-1">No buildings found</p>
            <p className="text-sm">Create a pocket to get started.</p>
          </Card>
        ) : (
          filteredGroups.map(group => (
            // Not wrapped in <Card>: this whole tile is the click target
            // (react-router `Link`, i.e. an <a>), and Card only renders a
            // <div> — nesting one inside would either lose the anchor as
            // the visual card boundary or require restructuring the
            // group-hover wiring below. Retokened bg-white/border-gray-200
            // directly instead, matching Card's own bg-surface-raised/
            // border-border-token pairing.
            <Link
              key={group.building_asset_id}
              to={`/pockets/building/${group.building_asset_id}`}
              className="bg-surface-raised rounded-xl shadow-sm border border-border-token p-6 hover:shadow-md hover:border-primary-300 transition-all group"
            >
              {/* Building icon */}
              <div className="flex items-start justify-between mb-4">
                {/* bg-primary-100 is a literal Tailwind colour (not a
                    CSS-var token) so it doesn't auto-switch with the theme.
                    dark:bg-primary-900 (#134e4a, same hex as Badge's
                    success-tone dark fill) keeps text-accent's dark value
                    (teal-400) legible on it — 5.09:1 computed by hand. */}
                <div className="w-10 h-10 bg-primary-100 dark:bg-primary-900 rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-accent" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                </div>
                <svg className="w-5 h-5 text-content-muted group-hover:text-primary-500 transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>

              {/* I4 (dark-mode contrast fix): group-hover:text-primary-700 has
                  no dark: counterpart — 2.00:1 against this card's
                  surfaceRaised(dark), fails AA. dark:group-hover:text-accent
                  uses the theme-aware accent token instead of a hand-picked
                  primary shade (accent dark = teal-400, 8.15:1 against
                  surfaceRaised(dark)); light mode is untouched (primary-700
                  vs white is 7.58:1, already fine). */}
              <h3 className="text-lg font-semibold text-content mb-2 group-hover:text-primary-700 dark:group-hover:text-accent transition-colors">
                {group.building_name}
              </h3>

              <div className="flex items-center gap-4">
                <div className="flex items-center gap-1.5">
                  <span className="w-2 h-2 bg-primary-500 rounded-full"></span>
                  <span className="text-sm text-content-muted">{group.pockets.length} pocket{group.pockets.length !== 1 ? 's' : ''}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                  <span className="text-sm text-content-muted">{group.total_meters} meter{group.total_meters !== 1 ? 's' : ''}</span>
                </div>
              </div>

              {/* Pocket names preview with occupancy dot — routed through
                  Badge (success/warning tones) per the Badge-adoption
                  instruction; the occupancy dot is preserved as a Badge
                  child rather than dropped, since it's the primary visual
                  signal here (colour + shape), not just decoration. */}
              <div className="mt-3 flex flex-wrap gap-1.5">
                {group.pockets.slice(0, 4).map(p => {
                  const occupied = !!p.is_occupied;
                  const label = occupied ? (p.active_tenant_name || p.name) : 'Vacant';
                  return (
                    <Badge
                      key={p.id}
                      tone={occupied ? 'success' : 'warning'}
                      className="gap-1.5"
                      title={occupied
                        ? (p.active_tenant_end_date
                          ? `Lease ends ${p.active_tenant_end_date}`
                          : 'Open-ended lease')
                        : 'Vacant — no active tenant'}
                    >
                      <span
                        className={`inline-block w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                          occupied ? 'bg-teal-600 dark:bg-teal-300' : 'bg-amber-600 dark:bg-amber-300'
                        }`}
                      />
                      {/* Badge sets whitespace-nowrap unconditionally, and
                          nothing upstream of this grid clips overflow — a
                          long tenant trading name with no natural break
                          point could otherwise force this card (and the
                          grid track under it) wider than the viewport at
                          375px, the same class of bug flagged for DataTable.
                          Bounding + truncating just the label text (not the
                          whole Badge) keeps the dot visible and the chip
                          readable instead of blowing out the layout. */}
                      <span className="truncate max-w-[9rem] inline-block align-bottom">{label}</span>
                    </Badge>
                  );
                })}
                {group.pockets.length > 4 && (
                  <Badge tone="neutral">+{group.pockets.length - 4} more</Badge>
                )}
              </div>
            </Link>
          ))
        )}
      </div>

      {/* Create Pocket Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          {/* Plain bg-surface-raised div, not <Card> — matches the modal
              precedent from TariffManagement/Tenants (fixed inset-0
              overlays keep their own rounded-xl/shadow-2xl rather than
              inheriting Card's rounded-lg). */}
          <div className="bg-surface-raised rounded-xl shadow-2xl w-full max-w-md p-6">
            <h2 className="text-lg font-bold text-content mb-4">Create Pocket</h2>
            {createError && <Alert tone="danger" className="mb-4">{createError}</Alert>}
            <form onSubmit={handleCreatePocket}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-content mb-1">Building *</label>
                  <select required value={createForm.building_asset_id} onChange={e => setCreateForm(p => ({ ...p, building_asset_id: e.target.value }))}
                    className="w-full px-3 py-2 min-h-tap lg:min-h-0 border border-border-token rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent">
                    <option value="">Select a building...</option>
                    {buildings.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-content mb-1">Pocket Name *</label>
                  <input type="text" required value={createForm.name} onChange={e => setCreateForm(p => ({ ...p, name: e.target.value }))}
                    className="w-full px-3 py-2 min-h-tap lg:min-h-0 border border-border-token rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-content mb-1">Unit / Shop No</label>
                  <input type="text" value={createForm.shop_no} onChange={e => setCreateForm(p => ({ ...p, shop_no: e.target.value }))}
                    className="w-full px-3 py-2 min-h-tap lg:min-h-0 border border-border-token rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-content mb-1">Description</label>
                  <textarea value={createForm.description} onChange={e => setCreateForm(p => ({ ...p, description: e.target.value }))} rows={2}
                    className="w-full px-3 py-2 border border-border-token rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent" />
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <button type="button" onClick={() => setShowCreateModal(false)} className="px-4 py-2 min-h-tap lg:min-h-0 inline-flex items-center justify-center text-content bg-surface-sunken rounded-lg hover:bg-border-token">Cancel</button>
                <button type="submit" disabled={creating} className="px-4 py-2 min-h-tap lg:min-h-0 inline-flex items-center justify-center bg-accent text-accent-fg rounded-lg hover:opacity-90 disabled:opacity-50">
                  {creating ? 'Creating...' : 'Create Pocket'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
